import { Autocomplete, AutocompleteRenderGroupParams, Stack, TextField } from "@mui/material";
import { styled, useTheme } from "@mui/system";
import React from "react";
import { useTranslation } from "react-i18next";
import { AutocompleteHighlight } from "../../../shared/AutocompleteHighlight";
import { getAssetTypeLabel } from "../../../shared/constants/managedSurveyAssetTypes";
import { Asset } from "../../../shared/interfaces/Asset";
import { ValidationMessage } from "../../../shared/ValidationMessage";

interface MultiSelectAssetDropdownProps {
  onAssetChange: (selectedAssets: Asset[]) => void;
  width?: string;
  invalid?: boolean;
  invalidMessage: string;
  disabled?: boolean;
  selectedAssets?: Asset[];
  options: Asset[];
  disableErrorSpace?: boolean;
}

const GroupHeader = styled("div")(({ theme }) => ({
  padding: "4px 10px",
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.primary.contrastText,
}));

const GroupItems = styled("ul")({
  padding: 0,
});

export default function MultiSelectAssetDropdown(props: MultiSelectAssetDropdownProps) {
  const { t } = useTranslation();
  const selectedAssets = props.selectedAssets ?? [];
  const theme = useTheme();

  const handleChange = (_event: React.SyntheticEvent, newValue: Asset[] | null) => {
    props.onAssetChange(newValue ?? []);
  };

  return (
    <Stack
      direction="column"
      sx={{
        width: props.width,
        minHeight: props.disableErrorSpace ? "calc(100%)" : "calc(100% + 20px)",
        paddingTop: props.disableErrorSpace ? "0px" : "20px",
      }}
    >
      <Autocomplete
        value={selectedAssets}
        autoHighlight
        blurOnSelect={false}
        disabled={props.disabled}
        size="small"
        multiple
        renderInput={(params) => (
          <TextField {...params} placeholder={selectedAssets.length === 0 ? t("asset.selectAssets") : ""} />
        )}
        options={props.options}
        groupBy={(option: Asset) => option.assetTypeKey}
        getOptionLabel={(option: Asset) => option.name}
        onChange={handleChange}
        isOptionEqualToValue={(option: Asset, value: Asset) => option.id === value.id}
        sx={{ width: props.width || 300, backgroundColor: theme.palette.primary.contrastText }}
        renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: Asset, { selected, inputValue }) => (
          <AutocompleteHighlight
            renderProps={props}
            optionLabel={option.name}
            inputValue={inputValue}
            multiSelect={true}
            selected={selected}
          />
        )}
        renderGroup={(params: AutocompleteRenderGroupParams) => (
          <li key={params.key}>
            <GroupHeader>{t(`assetTypes.${getAssetTypeLabel(params.group)}`)}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
      />
      <div style={{ minHeight: props.disableErrorSpace ? "0px" : "20px" }}>
        {props.invalid && props.invalidMessage && (
          <ValidationMessage invalidMessage={props.invalidMessage} validationType="error" showIcon />
        )}
      </div>
    </Stack>
  );
}
